import React, { useContext } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import {
    ArAgingSummaryTableCallbacks,
    getArAgingSummaryDrillDownParams,
} from "./arAgingDetailDrillDown.lib";
import { ArAgingSummaryViewRow } from "./arAgingSummaryViewTypes";
import styles from "./ArAgingSummaryTable.module.scss";

/**
 * The row Total cell drills into AR Aging Detail scoped to the customer,
 * across every bucket (the customer's Total row analog, no `bucket` param).
 * The synthetic Total row's Total cell drills into the unscoped Detail
 * report (every customer, every bucket).
 */
export const ArAgingSummaryTotalCell = React.memo(
    function ArAgingSummaryTotalCell({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ArAgingSummaryViewRow,
        unknown
    >): React.ReactElement {
        const { total, customerId, isSummary } = row.original;
        const { filters } = useContext(AccountingReportContext);
        const onDrillDown = useViewTableMeta<
            ArAgingSummaryTableCallbacks,
            unknown
        >(table).callbacks?.onDrillDown;
        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(total);

        const drillDownParams = getArAgingSummaryDrillDownParams({
            filters,
            customerId,
            isSummary,
        });

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.summaryCell]: isSummary,
                })}
            >
                {onDrillDown ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={() => onDrillDown(drillDownParams)}
                    >
                        {displayValue}
                    </button>
                ) : (
                    displayValue
                )}
            </ViewTableCell>
        );
    },
);
